import { FormatterService } from "../NumberFormatter";
import { GenericObject } from "./Price";
import { checkErrors, getDefaultOptions } from "./Category";

export interface GenericDjangoPaginatedResponse<T> {
  count: number;
  previous: string | null;
  next: string | null;
  results: Array<T>;
}

export interface GenericPaginatedResponse<T> {
  responseSuccess: number;
  responseMessage: string | null;
  responseCount: number;
  responseContent: Array<T>;
}

export interface GenericResponse<T> {
  responseSuccess: number;
  responseMessage: string | null;
  responseContent: T;
}

export interface ErrorResponse {
  error: string;
}

type GetDivisor =
  | { value: number; symbol: string; digits?: undefined }
  | { value: number; symbol: string; digits: number }
  | undefined;

/**
 * Get the divisor for a value.
 */
export const getDivisor = (
  value: number,
  kUnit: boolean = false
): GetDivisor => {
  const absoluteValue = Math.abs(value);

  if (kUnit) {
    return {
      value: 1000,
      symbol: "k",
      digits: 1,
    };
  }

  return [
    {
      value: 1,
      symbol: "",
    },
    {
      value: 1e3,
      symbol: "k",
      digits: 1,
    },
    {
      value: 1e6,
      symbol: "M",
    },
  ]
    .slice()
    .reverse()
    .find((item) => absoluteValue >= item.value);
};

/**
 * Format a number with a unit.
 */
export const nFormatter = (
  num: number,
  formatter: FormatterService
): string => {
  const item = getDivisor(num);

  if (item) {
    return (
      formatter.format(num / item.value, { maximumSignificantDigits: 3 }) +
      " " +
      item.symbol
    );
  }

  return "0";
};

/**
 * Format a value with a unit.
 */
export const formatValue = (value: number, unit: string): string => {
  const val = Math.round(value);

  return `${val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} ${unit}`;
};

/**
 * Normalize a string to be used as an id for translation.
 */
export const normalizeString = (str: string): string =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s/g, "")
    .replace(/-/g, "")
    .replace(/_/g, "")
    .replace(/'/g, "")
    .replace(/"/g, "")
    .toLowerCase();

/**
 * Check if the object is an object and not an array.
 */
export const isObject = (obj: any) =>
  typeof obj === "object" && obj !== null && !Array.isArray(obj);

/**
 * Replace all non-numeric characters from a string.
 */
export const replaceNonNumeric = (value: string): string =>
  value?.replace(/[^0-9]/g, "");

/**
 * Capitalize first letter
 *
 * @param string
 * @returns string with capitalized first letter
 */
export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/**
 * Replace all non-numeric characters from a string, except minus, comma and dot, but only keeps the last non-final comma/dot
 * https://stackoverflow.com/questions/58413362/how-to-remove-all-non-numeric-characters-excluding-minus-dot-and-comma-in-a-s
 */
export const replaceNonNumericExceptMinus = (value: string): string =>
  value?.replace(/^(-)|[.,](?=[^.,]*[.,](?!$))|[,.]+$|[^0-9.,]+/g, "$1");

export const simpleQuery = async (url: string, options?: GenericObject) => {
  const response = await fetch(url, { ...getDefaultOptions(), ...options });

  checkErrors(response);

  return response.json();
};

/**
 * Set a cookie, by default with expire date of a month
 *
 * @param name - Name of cookie
 * @param value - Value of cookie
 * @param expireInDays - Number of days before expiration
 */
export const setCookie = (
  name: string,
  value: string,
  expireInDays?: number
) => {
  let expires = "";
  const expiryDate = new Date();

  if (expireInDays !== undefined) {
    expiryDate.setDate(expiryDate.getDate() + expireInDays);
    expires = "; expires=" + expiryDate.toUTCString();
  } else {
    expiryDate.setMonth(expiryDate.getMonth() + 1);
    expires = "; expires=" + expiryDate.toUTCString();
  }

  document.cookie = name + "=" + value + expires + "; path=/";
};

/**
 * Get cookie value
 *
 * @param name
 */
export const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const cookie = parts.pop();

    if (cookie) {
      return cookie.split(";").shift();
    }
  }

  return null;
};

export const DIALOG_PARAM = "dialog";
export const DIALOG_ID_PARAM = "dialogId";
export const TAB_PARAM = "tab";

export const slugify = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const getSearchParam = (key: string) =>
  new URL(window.location.href).searchParams.get(key);
